// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The end of a scan, however the scanner says it — or does not.
 *
 * A keyboard-wedge scanner types the serial very fast, and is usually set up to press Enter
 * after it. Three ways that Enter fails to arrive, all seen on the shop floor:
 *
 * - **No suffix configured.** The scanner types the serial and stops. Nothing but the
 *   typing itself says the scan is over.
 * - **A Chinese input method is on.** The letters go into its composition and the Enter
 *   commits them: the keydown arrives as `Process` (keyCode 229) or flagged `isComposing`.
 *   Only the keyup is a plain Enter.
 * - **A plain Enter**, the case that always worked.
 *
 * So a scan ends at whichever comes first: an Enter keydown, an Enter keyup whose keydown
 * the IME took, or a **burst** — at least `MIN_SCAN_LENGTH` characters typed no slower than
 * `MAX_SCAN_GAP_MS` apart on average, followed by `SCAN_IDLE_MS` of quiet. A person typing
 * is several times slower than that and ends with Enter, as before; a paste counts as a
 * scan. Each scan is acted on once: an Enter cancels its burst, and the keyup of an Enter
 * already acted on is skipped — shared across boxes, because the keydown may have moved
 * focus to the next one.
 */

import type { CompositionEvent, FormEvent, KeyboardEvent } from "react";

export const SCAN_IDLE_MS = 120;
export const MIN_SCAN_LENGTH = 6;
/** A USB scanner types a character every 5–15 ms; a fast typist manages a short run of
 *  keys at 50–80 ms. Thirty keeps the two apart, so a person pausing mid-word is not taken
 *  for a finished scan and moved on. */
export const MAX_SCAN_GAP_MS = 30;

interface Burst {
  first: number;
  last: number;
  inputs: number;
  startLength: number;
  timer: ReturnType<typeof setTimeout>;
}

const bursts = new WeakMap<HTMLElement, Burst>();
/** Boxes an input method is composing in: their text is not final until it commits. */
const composing = new WeakSet<HTMLElement>();
let handledOnKeyDown = false;

function isComposing(event: KeyboardEvent): boolean {
  return event.nativeEvent.isComposing || event.keyCode === 229;
}

function cancelBurst(element: HTMLElement) {
  const burst = bursts.get(element);
  if (burst) clearTimeout(burst.timer);
  bursts.delete(element);
}

export function scanEnterHandlers<Element extends HTMLInputElement>(
  onEnd: (target: Element) => void,
  /** Also end a scan at the pause after a burst. For boxes that take scans only — not a
   *  field people type into, where a pause would move them on mid-entry. */
  { burst: endOnPause = false }: { burst?: boolean } = {},
) {
  const end = (target: Element) => {
    cancelBurst(target);
    onEnd(target);
  };
  return {
    onKeyDown(event: KeyboardEvent<Element>) {
      // Any other key starts the next scan: whatever Enter came before is done with, even
      // if its keyup landed somewhere that does not listen.
      if (event.key !== "Enter") handledOnKeyDown = false;
      if (event.key !== "Enter" || isComposing(event)) return;
      // In a form, Enter would submit it half-filled; here it ends a scan.
      event.preventDefault();
      handledOnKeyDown = true;
      end(event.currentTarget);
    },
    onKeyUp(event: KeyboardEvent<Element>) {
      if (event.key !== "Enter") return;
      if (handledOnKeyDown) {
        handledOnKeyDown = false;
        return;
      }
      end(event.currentTarget);
    },
    onCompositionStart(event: CompositionEvent<Element>) {
      composing.add(event.currentTarget);
    },
    onCompositionEnd(event: CompositionEvent<Element>) {
      composing.delete(event.currentTarget);
    },
    onInput(event: FormEvent<Element>) {
      if (!endOnPause) return;
      const target = event.currentTarget;
      const now = Date.now();
      const inserted = (event.nativeEvent as InputEvent).data?.length ?? 1;
      const previous = bursts.get(target);
      if (previous) clearTimeout(previous.timer);
      const burst: Omit<Burst, "timer"> =
        previous && now - previous.last <= SCAN_IDLE_MS
          ? { ...previous, last: now, inputs: previous.inputs + 1 }
          : {
              first: now,
              last: now,
              inputs: 1,
              startLength: Math.max(target.value.length - inserted, 0),
            };
      const timer = setTimeout(function settle() {
        // Mid-composition the text is still the IME's; wait for it to commit.
        if (composing.has(target)) {
          bursts.set(target, { ...burst, timer: setTimeout(settle, SCAN_IDLE_MS) });
          return;
        }
        bursts.delete(target);
        const typed = target.value.length - burst.startLength;
        const gap = (burst.last - burst.first) / Math.max(burst.inputs - 1, 1);
        if (typed >= MIN_SCAN_LENGTH && gap <= MAX_SCAN_GAP_MS) onEnd(target);
      }, SCAN_IDLE_MS);
      bursts.set(target, { ...burst, timer });
    },
  };
}
